// Exportación del informe de "Ventas y despachos" — CSV (una fila por
// despacho) y PDF (una constancia por despacho: encabezado + detalle de
// animales con pesos). Funciona 100% offline: todo se genera en el
// navegador a partir de los datos ya sincronizados en Dexie.
import { jsPDF } from "jspdf";
import { autoTable } from "jspdf-autotable";
import { formatDate, formatDateTime } from "@/lib/utils";

function csvCell(v) {
  return `"${String(v ?? "").replace(/"/g, '""')}"`;
}

export function exportDispatchesCsv(items, farmName) {
  const header = [
    "Fecha", "Tipo", "Destino / comprador", "Finca de origen", "Cantidad de animales",
    "Peso total (kg)", "Peso promedio (kg)", "Guía INSAI", "Fecha guía", "Chofer", "Placa",
    "Registrado por", "Importe",
  ];
  const rows = items.map((i) => [
    formatDate(i.date), i.type_label, i.destination, farmName(i.farm_id),
    i.animal_count, i.total_weight || "", i.avg_weight || "", i.guide_number || "",
    i.guide_date ? formatDate(i.guide_date) : "", i.driver || "", i.plate || "",
    i.registered_by_name || "", i.amount != null ? i.amount : "",
  ]);
  const csv = [header, ...rows].map((r) => r.map(csvCell).join(",")).join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = `ventas-y-despachos-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
  URL.revokeObjectURL(url);
}

function drawDispatchHeader(doc, item, farmName, y) {
  doc.setFontSize(14);
  doc.setFont(undefined, "bold");
  doc.text(`Constancia de despacho — ${item.type_label}`, 14, y);
  doc.setFontSize(10);
  doc.setFont(undefined, "normal");
  let ly = y + 8;
  const line = (label, value) => {
    if (!value) return;
    doc.text(`${label}: ${value}`, 14, ly);
    ly += 5.5;
  };
  line("Finca de origen", farmName(item.farm_id));
  line("Fecha", formatDate(item.date));
  line("Destino / comprador", item.destination);
  line("N° de Guía INSAI", item.guide_number);
  line("Fecha de guía", item.guide_date ? formatDate(item.guide_date) : null);
  line("Chofer", item.driver ? `${item.driver}${item.driver_id ? ` (C.I. ${item.driver_id})` : ""}` : null);
  line("Placa", item.plate);
  line("Registrado por", item.registered_by_name);
  return ly + 2;
}

function drawDispatchTable(doc, item, y) {
  const rows = item.animals.map((a) => [
    a.chip_code || "—", a.lomo_number || "—", a.ear_tattoo || "—", a.category || "—",
    a.weight_kg != null ? String(a.weight_kg) : "—",
  ]);
  autoTable(doc, {
    startY: y,
    head: [["Chip", "Lomo", "Tatuaje", "Categoría", "Peso (kg)"]],
    body: rows,
    styles: { fontSize: 9 },
    headStyles: { fillColor: [61, 79, 61] },
    margin: { left: 14, right: 14 },
  });
  const finalY = doc.lastAutoTable.finalY + 6;
  doc.setFontSize(10);
  doc.setFont(undefined, "bold");
  const totalsLine = [`Total animales: ${item.animal_count}`, `Peso total: ${item.total_weight || 0} kg`];
  if (item.amount != null) totalsLine.push(`Importe: $ ${item.amount}`);
  doc.text(totalsLine.join("   ·   "), 14, finalY);
  doc.setFont(undefined, "normal");
  return finalY;
}

/** Genera un PDF con una constancia por cada despacho de la lista (uno o varios). */
export function exportDispatchesPdf(items, farmName) {
  const doc = new jsPDF();
  items.forEach((item, idx) => {
    if (idx > 0) doc.addPage();
    const afterHeader = drawDispatchHeader(doc, item, farmName, 18);
    drawDispatchTable(doc, item, afterHeader);
  });
  const suffix = items.length === 1 ? (items[0].guide_number || items[0].id.slice(0, 8)) : new Date().toISOString().slice(0, 10);
  doc.save(`despacho-${suffix}.pdf`);
}
